var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { css, keyframes } from 'goober';
import { isEmpty } from 'mc-ui-utils';
import React, { memo, useEffect, useRef } from 'react';
import posterImgFallback from '../../assets/fallback.png';
import posterImg from '../../assets/spin-poster-small.svg';
import { useVideoListeners } from '../../hooks/useVideoListeners';
import State from '../../state';
import colors from '../../styles/colors';
import { isIOS, resetVideo } from '../../utils/videohelper';
import { connectComponent } from '../Provider';
const Container = css `
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Video = css `
  pointer-events: none;
  height: 100%;
  width: 100%;
`;
const LoaderAnimation = keyframes `
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;
const Loader = css `
  border: 3px solid ${colors.concrete};
  animation: ${LoaderAnimation} 1s linear infinite;
  border-top: 3px solid ${colors.emperor};
  border-radius: 50%;
  width: 50px;
  height: 50px;
  position: absolute;
  left: calc(50% - 25px);
  top: calc(50% - 25px);
`;
const pauseVideo = (videoRef, promiseRef) => {
    if (promiseRef !== undefined) {
        promiseRef
            .then(() => {
            videoRef.pause();
        })
            .catch((err) => {
            console.log(`Play call failed, ${err}`);
        });
    }
    else {
        videoRef.pause();
    }
};
const NativeVideo = (props) => {
    const { videoUrl, isMuted, glanceId, isCurrentGlance, isHolding, bubbleEvent, shouldPreload, isHomeVisible, informStateUpdatedOnInvisible, dashManifestUrl, } = props;
    const videoRef = useRef();
    const isActiveGlance = useRef(isCurrentGlance); // keeping old value of isCurrentGlance, to do sort of shouldComponentUpdate
    const canUseShaka = useRef(!isEmpty(dashManifestUrl)); // shaka player will not be used if dashManifestUrl is not present
    const playPromiseRef = useRef();
    const { isReady, hasStartedPlaying, startTime, loadingTime, playCallDuration, playStartDuration, videoErrorCount, onBuffering, onPlayingShaka, } = useVideoListeners(videoRef, glanceId, shouldPreload, isActiveGlance);
    useEffect(() => {
        if (!dashManifestUrl || isIOS()) {
            canUseShaka.current = false;
            return;
        }
        let isShakaSupported = false;
        // typescript definitions of shaka player are broken
        // Refer https://github.com/shaka-project/shaka-player/pull/3331
        // @ts-ignore
        import('shaka-player').then((shaka) => {
            function initApp() {
                // Install built-in polyfills to patch browser incompatibilities.
                shaka.polyfill.installAll();
                // Check to see if the browser supports the basic APIs Shaka needs.
                if (shaka.Player.isBrowserSupported()) {
                    // Everything looks good!
                    isShakaSupported = true;
                    console.info('Yay! Browser supports shaka player!');
                }
                else {
                    // This browser does not have the minimum set of APIs we need.
                    canUseShaka.current = false;
                    console.info('Oops! Browser does not support shaka player!');
                }
            }
            // @ts-ignore
            function initPlayer(videoEL, manifestUri) {
                return __awaiter(this, void 0, void 0, function* () {
                    // Create a Player instance.
                    const player = new shaka.Player(videoEL);
                    player.configure('manifest.dash.ignoreMinBufferTime', true);
                    player.configure({
                        streaming: {
                            bufferingGoal: 4,
                            rebufferingGoal: 2,
                            //inaccurateManifestTolerance: 0,
                            retryParameters: {
                                maxAttempts: 1, // the maximum number of requests before we fail
                            },
                        },
                        abr: {
                            defaultBandwidthEstimate: 500000,
                        },
                    });
                    // Attach player to the window to make it easy to access in the JS console.
                    // @ts-ignore
                    window.player = player;
                    // Listen for error events.
                    player.addEventListener('error', onErrorEvent);
                    // Try to load a manifest.
                    // This is an asynchronous process.
                    try {
                        yield player.load(manifestUri);
                        // Listen for buffering event
                        player.addEventListener('buffering', () => {
                            player.isBuffering() ? onBuffering() : onPlayingShaka();
                        });
                    }
                    catch (e) {
                        // onError is executed if the asynchronous load fails.
                        onError(e);
                    }
                });
            }
            // @ts-ignore
            function onErrorEvent(event) {
                // Extract the shaka.util.Error object from the event.
                onError(event.detail);
            }
            // @ts-ignore
            function onError(error) {
                // Log the error.
                console.error('Error code', error.code, 'object', error);
            }
            typeof window !== 'undefined' && initApp();
            if (isShakaSupported) {
                initPlayer(videoRef.current, dashManifestUrl);
            }
        });
    }, []);
    useEffect(() => {
        var _a;
        /**
         * we are not adding isCurrentGlance as a dependency as we want this useEffect to be executed
         * when isHomeVisible is changed from true to false.
         * we do not want this to run whenever isCurrentGlance changes.
         */
        if (!isHomeVisible && isCurrentGlance) {
            if (!((_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.paused)) {
                pauseVideo(videoRef.current, playPromiseRef.current);
            }
            fireVideoDetails();
            informStateUpdatedOnInvisible();
        }
        else {
            if (isHomeVisible &&
                isCurrentGlance &&
                !isHolding &&
                videoRef.current &&
                videoRef.current.paused) {
                playPromiseRef.current = videoRef.current.play();
            }
        }
    }, [isHomeVisible]);
    useEffect(() => {
        if (videoRef.current &&
            isActiveGlance.current &&
            videoRef.current.muted !== isMuted) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted]);
    const fireVideoDetails = () => {
        var _a;
        const playbackTime = (_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.currentTime;
        const totalVideoWatchDuration = Math.round((playbackTime - (startTime.current ? startTime.current : 0)) * 1000);
        const videoTime = startTime.current
            ? Math.round(startTime.current * 1000)
            : 0;
        const videoDetails = {
            videoDuration: Math.round(State.getVideoDuration(glanceId)),
            videoFailureCount: videoErrorCount.current,
            videoLoadDuration: loadingTime.current,
            playCallDuration: playCallDuration.current,
            playStartDuration: playStartDuration.current,
            videoTime: videoTime,
            totalVideoWatchDuration,
        };
        bubbleEvent.setGlanceVideoDetails(glanceId, videoDetails);
    };
    useEffect(() => {
        var _a;
        if (!isEmpty(videoRef.current) && isActiveGlance.current) {
            if (isHolding) {
                if (!videoRef.current.paused) {
                    pauseVideo(videoRef.current, playPromiseRef.current);
                }
            }
            else {
                if (videoRef.current.paused) {
                    playPromiseRef.current = (_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.play();
                }
            }
        }
    }, [isHolding]);
    // eslint-disable-next-line sonarjs/cognitive-complexity
    useEffect(() => {
        const videoPlayer = videoRef.current;
        if (isCurrentGlance !== isActiveGlance.current) {
            if (!isCurrentGlance) {
                if (!isEmpty(videoPlayer) && !videoPlayer.paused) {
                    pauseVideo(videoPlayer, playPromiseRef.current);
                }
                if (isActiveGlance.current) {
                    // current glance has changed to inactive glance
                    fireVideoDetails();
                    if (!isEmpty(videoPlayer)) {
                        const currentTime = videoPlayer.currentTime;
                        resetVideo(videoRef.current, glanceId, currentTime);
                    }
                }
            }
            isActiveGlance.current = isCurrentGlance;
        }
        if (isCurrentGlance && videoPlayer) {
            const toggling = Date.now();
            if (videoPlayer.paused) {
                const videoTimeline = State.getVideoTimeline(glanceId);
                if (!isEmpty(videoTimeline)) {
                    startTime.current = videoTimeline;
                    if (!isEmpty(videoPlayer)) {
                        videoPlayer.currentTime = videoTimeline;
                    }
                }
                if (!isEmpty(videoPlayer)) {
                    playPromiseRef.current = videoPlayer.play();
                    // time elapsed between the start of a glance and user/auto clicking on play button
                    playCallDuration.current = Date.now() - toggling;
                    if (playPromiseRef.current !== undefined) {
                        playPromiseRef.current
                            .then(() => {
                            // Automatic playback started!
                            playStartDuration.current = Date.now() - toggling;
                        })
                            .catch(() => {
                            playStartDuration.current = null; // this means play never started.
                        });
                    }
                }
            }
            else {
                playCallDuration.current = 0; // video was playing alredy. Play didn't need to be called.
                playStartDuration.current = 0; // video had started alredy.
            }
        }
    }, [isCurrentGlance, glanceId, bubbleEvent]);
    if (isEmpty(videoUrl)) {
        return null;
    }
    return (React.createElement("div", { className: Container },
        React.createElement("video", { poster: !isReady && !hasStartedPlaying ? posterImg : posterImgFallback, className: Video, ref: videoRef, muted: isMuted, controls: false, loop: true, preload: shouldPreload ? 'metadata' : 'none', playsInline: true }, !canUseShaka.current && React.createElement("source", { src: `${videoUrl}#t=0.1` })),
        !isReady && hasStartedPlaying && React.createElement("div", { className: Loader })));
};
const mapStateToProps = (state) => ({
    isMuted: state.isMuted,
});
const arePropsEqual = (prevProps, nextProps) => {
    return (prevProps.isCurrentGlance === nextProps.isCurrentGlance &&
        prevProps.shouldPreload === nextProps.shouldPreload &&
        prevProps.isHolding === nextProps.isHolding &&
        prevProps.isHomeVisible === nextProps.isHomeVisible);
};
export default memo(connectComponent(mapStateToProps)(NativeVideo), arePropsEqual);
//# sourceMappingURL=index.js.map